import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MapPinIcon, ShieldCheckIcon, SparklesIcon, CheckCircle2Icon, MessageSquareIcon, PhoneIcon, HeartIcon, Share2Icon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ListingCardProps } from './ListingCard';
interface ListingDetailModalProps {
  listing: ListingCardProps;
  open: boolean;
  onClose: () => void;
}
export function ListingDetailModal({
  listing,
  open,
  onClose
}: ListingDetailModalProps) {
  const [activeImage, setActiveImage] = useState(0);
  const [saved, setSaved] = useState(listing.isSaved ?? false);
  const aiSummary = ['Fair price based on current market comparison', 'GCC specifications confirmed', 'Mileage is reasonable for the model year', 'Seller response rate is high'];
  const prev = () => setActiveImage(i => (i - 1 + listing.images.length) % listing.images.length);
  const next = () => setActiveImage(i => (i + 1) % listing.images.length);
  return createPortal(<AnimatePresence data-id="element-1864">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 lg:p-8" data-id="element-1865">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-1866" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 320
      }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl h-[88vh] overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-label={listing.title} data-id="element-1867">
            {/* Close */}
            <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 z-20 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-1868">
              <XIcon className="w-5 h-5 text-slate-700" data-id="element-1869" />
            </button>

            <div className="overflow-y-auto" data-id="element-1870">
              {/* Gallery */}
              <div className="relative aspect-[16/9] bg-slate-100" data-id="element-1871">
                <img src={listing.images[activeImage]} alt={listing.title} className="w-full h-full object-cover" data-id="element-1872" />
                {listing.images.length > 1 && <>
                    <button onClick={prev} aria-label="Previous image" className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-white/85 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-1873">
                      <ChevronLeftIcon className="w-5 h-5 text-slate-700" data-id="element-1874" />
                    </button>
                    <button onClick={next} aria-label="Next image" className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-white/85 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-1875">
                      <ChevronRightIcon className="w-5 h-5 text-slate-700" data-id="element-1876" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-1877">
                      {listing.images.map((_, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`h-1.5 rounded-full transition-all ${idx === activeImage ? 'bg-white w-4' : 'bg-white/50 w-1.5'}`} data-id="element-1878" />)}
                    </div>
                  </>}
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm" data-id="element-1879">
                  <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-1880" />
                  <span className="text-sm font-bold bg-gradient-to-r from-primary-indigo to-ai-purple bg-clip-text text-transparent" data-id="element-1881">
                    {listing.matchScore}% Match
                  </span>
                </div>
              </div>

              <div className="p-6 lg:p-8 space-y-6" data-id="element-1882">
                {/* Title + Price */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-id="element-1883">
                  <div data-id="element-1884">
                    <h2 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-1885">
                      {listing.title}
                    </h2>
                    <div className="flex items-center gap-1.5 text-slate-500" data-id="element-1886">
                      <MapPinIcon className="w-4 h-4" data-id="element-1887" />
                      <span data-id="element-1888">{listing.location}</span>
                    </div>
                  </div>
                  <span className="font-bold text-3xl text-primary-indigo whitespace-nowrap" data-id="element-1889">
                    {listing.price}
                  </span>
                </div>

                {/* AI Summary */}
                <div className="bg-gradient-to-br from-primary-indigo/5 via-ai-purple/5 to-electric-cyan/5 rounded-2xl p-5 border border-primary-indigo/10" data-id="element-1890">
                  <div className="flex items-center gap-2 mb-4" data-id="element-1891">
                    <div className="p-2 bg-gradient-to-br from-primary-indigo to-ai-purple rounded-xl" data-id="element-1892">
                      <SparklesIcon className="w-4 h-4 text-white" data-id="element-1893" />
                    </div>
                    <h3 className="font-display font-bold text-dark-navy" data-id="element-1894">
                      AI Analysis
                    </h3>
                  </div>
                  <ul className="space-y-2.5" data-id="element-1895">
                    {aiSummary.map((item, idx) => <li key={idx} className="flex items-start gap-2.5 text-slate-700 text-sm" data-id="element-1896">
                        <CheckCircle2Icon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-1897" />
                        <span data-id="element-1898">{item}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Key info grid */}
                <div data-id="element-1899">
                  <h3 className="font-display font-bold text-lg text-dark-navy mb-4" data-id="element-1900">
                    Key Information
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-1901">
                    {listing.specs.map((spec, idx) => <div key={idx} className="bg-slate-50 rounded-xl px-4 py-3" data-id="element-1902">
                        <span className="block text-xs text-slate-500 mb-1" data-id="element-1903">
                          {spec.label}
                        </span>
                        <span className="font-medium text-slate-900" data-id="element-1904">
                          {spec.value}
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* Seller */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200" data-id="element-1905">
                  <div className="flex items-center gap-4" data-id="element-1906">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-lg font-bold text-slate-400" data-id="element-1907">
                      {listing.title.charAt(0)}
                    </div>
                    <div data-id="element-1908">
                      <h4 className="font-bold text-dark-navy" data-id="element-1909">
                        {listing.sellerType === 'Verified Business' ? 'Verified Dealer' : listing.sellerType}
                      </h4>
                      <div className="flex items-center gap-1.5 text-sm mt-0.5" data-id="element-1910">
                        {listing.sellerType === 'Verified Business' ? <>
                            <ShieldCheckIcon className="w-4 h-4 text-success" data-id="element-1911" />
                            <span className="text-success font-medium" data-id="element-1912">
                              Trusted seller · 99% response rate
                            </span>
                          </> : <span className="text-slate-500" data-id="element-1913">
                            Private seller · usually replies within hours
                          </span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Actions */}
            <div className="border-t border-slate-100 p-4 lg:px-8 bg-white flex flex-wrap items-center gap-3" data-id="element-1914">
              <button className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors" data-id="element-1915">
                <MessageSquareIcon className="w-5 h-5" data-id="element-1916" />
                Message Seller
              </button>
              <button className="flex items-center justify-center gap-2 py-3 px-4 bg-[#25D366] text-white rounded-xl font-medium hover:bg-[#20BD5A] transition-colors" data-id="element-1917">
                <PhoneIcon className="w-5 h-5" data-id="element-1918" />
                WhatsApp
              </button>
              <button onClick={() => setSaved(s => !s)} className="flex items-center justify-center gap-2 py-3 px-4 bg-slate-50 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors" data-id="element-1919">
                <HeartIcon className={`w-5 h-5 ${saved ? 'fill-error text-error' : ''}`} data-id="element-1920" />
                {saved ? 'Saved' : 'Save'}
              </button>
              <button className="flex items-center justify-center gap-2 py-3 px-4 bg-slate-50 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors" data-id="element-1921">
                <Share2Icon className="w-5 h-5" data-id="element-1922" />
                Share
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}